import React, { useEffect, useState } from 'react';
import { portalIdentityService, PortalIdentityView } from '../services/api';

// "Other ways to reach me" on the consent portal (person-identity plan,
// phase 4). One decision should cover a person however they reach the
// organisation, so they may add their other email addresses and chat
// accounts here — each proven with a one-time code sent to it.
//
// Holding the link is not proof of who you are (links get forwarded), so the
// address the invite names must be confirmed first, by typing it in full and
// entering the code sent to it; only then can others be added. Addresses
// already on file are shown masked for the same reason.

const SCHEME: Record<string, string> = {
  ns_uid: 'Phone system account', sip: 'Phone', tel: 'Phone', mailto: 'Email', matrix: 'Chat',
  wa_jid: 'WhatsApp', fb: 'Messenger', gchat: 'Google Chat',
};
const err = (e: any, f: string) => e?.response?.data?.error || f;

const PortalIdentityCard: React.FC<{ token: string }> = ({ token }) => {
  const [view, setView] = useState<PortalIdentityView | null>(null);
  const [address, setAddress] = useState('');
  const [challenge, setChallenge] = useState<{ id: string; masked: string; channel: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    portalIdentityService.view(token).then(setView).catch(e => setError(err(e, 'Could not load your addresses.')));
  }, [token]);

  if (!view) return error ? <div className="cp-card"><div className="error">{error}</div></div> : null;

  const anyChannel = view.channels.email || view.channels.matrix;
  const provable = view.identifiers.some(i => i.can_prove);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null); setNote(null);
    try { await fn(); } catch (e) { setError(err(e, 'That did not work; please try again.')); }
    setBusy(false);
  };
  const send = () => run(async () => {
    const r = await portalIdentityService.start(token, address.trim());
    setChallenge({ id: r.challenge_id, masked: r.masked, channel: r.channel });
    setCode('');
  });
  const confirm = () => run(async () => {
    const v = await portalIdentityService.verify(token, challenge!.id, code.trim());
    setView(v);
    setNote(`Confirmed: ${challenge!.masked}`);
    setChallenge(null); setAddress(''); setCode('');
  });
  const remove = (id: number) => run(async () => setView(await portalIdentityService.unlink(token, id)));

  return (
    <div className="cp-card">
      <div className="cp-card-h"><h3>Other ways to reach me</h3></div>
      <p className="cp-sub">
        Your decision covers every address on this list. If you also write to us from another
        email address, or chat with us, add it here so the same decision applies there too.
      </p>

      {view.identifiers.map(i => (
        <div key={i.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '4px 0' }}>
          <span className="small" style={{ minWidth: '9rem' }}>{SCHEME[i.scheme] || i.scheme}</span>
          <span className="monospace">{i.value}</span>
          <span className={i.proven ? 'status status-completed' : 'status'}>{i.proven ? 'confirmed' : 'not confirmed'}</span>
          {view.proven && i.id !== view.anchor_id && (
            <button type="button" disabled={busy} onClick={() => remove(i.id)}>Remove</button>
          )}
        </div>
      ))}

      {!anyChannel ? (
        <div className="cp-hint" style={{ display: 'block', marginTop: '10px' }}>
          Adding addresses here is not available yet.
        </div>
      ) : !view.proven && !provable ? (
        <div className="cp-hint" style={{ display: 'block', marginTop: '10px' }}>
          This invitation was sent to an address that cannot receive a code. If you have a
          NetSapiens account, you can link your other addresses from your Horizon account instead.
        </div>
      ) : (
        <div style={{ marginTop: '12px' }}>
          {!challenge ? (
            <>
              <label className="small" style={{ display: 'block' }}>
                {view.proven
                  ? <>Add an email address{view.channels.matrix ? ' or chat account' : ''}</>
                  : <>First, confirm it is you: type in full the address this invitation was sent to</>}
              </label>
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                <input type="text" value={address} disabled={busy} onChange={e => setAddress(e.target.value)}
                  placeholder={view.channels.matrix ? 'name@example.com or @name:matrix.12by9.com' : 'name@example.com'} style={{ minWidth: '18rem' }} />
                <button type="button" className="cp-gold-btn" disabled={busy || address.trim() === ''} onClick={send}>
                  {busy ? 'Sending…' : 'Send me a code'}
                </button>
              </div>
            </>
          ) : (
            <>
              <label className="small" style={{ display: 'block' }}>
                We sent a 6-digit code {challenge.channel === 'email' ? 'by email' : 'as a chat message'} to{' '}
                <span className="monospace">{challenge.masked}</span>. It expires in 15 minutes.
              </label>
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                <input type="text" inputMode="numeric" autoComplete="one-time-code" value={code} disabled={busy}
                  onChange={e => setCode(e.target.value)} placeholder="123456" style={{ width: '8rem' }} />
                <button type="button" className="cp-gold-btn" disabled={busy || code.trim().length < 6} onClick={confirm}>
                  Confirm
                </button>
                <button type="button" disabled={busy} onClick={() => { setChallenge(null); setCode(''); }}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}
      {note && <div className="cp-hint" style={{ display: 'block', marginTop: '8px' }}>{note}</div>}
      {error && <div className="error" style={{ marginTop: '8px' }}>{error}</div>}
    </div>
  );
};

export default PortalIdentityCard;
